import { BOLT_ENERGIE_CARE_PARTNER_ID, SessionStorageKeys } from 'constants/constants'
import { store } from 'store'
import Router from 'next/router'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import { CookieKeys } from 'types/cookies'
import { createCookie } from './cookies'
import {
  DeliveryAddressFieldKeys,
  FlowAlternatives,
  InputGroupKeys,
  MeterDetailsFieldKeys,
  PersonalDataFieldKeys,
  SimulationFieldKeys
} from 'store/customer/enums'
import { Language } from 'types/language'
import { loadDataByOpportunityId, setPromoOrReferralCodeInStore } from './customerFlow'
import { routes } from 'utils/routes'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { boltApi } from 'store/api/bolt-api'
import { updateOpportunityId } from 'store/app/slice'
import { setFlowAlternative, setShowPlentyPropositions, updateInput } from 'store/customer/slice'
import QueryString, { parse } from 'qs'
import { UrlSearchParams } from 'types/url'
import { AddressFields } from 'components/inputs/address/types'
import { CommonGTMEvents, CommonTrackingParams } from 'types/tracking'
import posthog from 'posthog-js'
import {
  IDENTIFICATION_METHODS,
  OPT_IN_CTAS,
  PRODUCTS_CONFIG,
  REGIONS,
  SignChannel,
  SIMULATION_TYPES,
  SimulationType
} from '@boltenergy-be/shared'
import { isAvailableSalesProduct, isValidAvailableProducts } from 'utils/parameters'
import { setSessionStorageItem } from 'utils/sessionStorage'
import { AvailableProducts } from 'types/parameters'

/**
 * Executes all needed tasks for startup
 */
export const startUp = (): void => {
  initStoreSubscription()

  // Add needed data to the store
  void store.dispatch(boltApi.endpoints.getProducers.initiate())

  // Fetch the URL params
  const parsedSearchQuery = parse(window.location.search, { ignoreQueryPrefix: true })

  // Check and save all URL params
  void checkAndSaveUrlParameters(parsedSearchQuery)

  // Set the registration language based on the current language
  store.dispatch(
    updateInput({
      group: InputGroupKeys.PERSONAL_DATA,
      key: PersonalDataFieldKeys.LANGUAGE,
      value: Router.locale || Language.DUTCH
    })
  )

  // Track assigned group & sourceType
  posthog.setPersonProperties({
    partner_id: parsedSearchQuery['partner_id'] as string | undefined,
    [CommonTrackingParams.SOURCE_TYPE]: parsedSearchQuery['source_type'] || store.getState().marketing.sourceType,
    is_care_user: parsedSearchQuery['partner_id'] === BOLT_ENERGIE_CARE_PARTNER_ID,
    _env: process.env.VERCEL_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV
  })
}

// USED STARTUP FUNCTIONS

/**
 * Checks and saves all the url paramaters if necessary
 *
 * @docs: https://www.notion.so/boltenergie/Website-URL-parameters-15f5f86ef80e8084911added16e26176?pvs=4
 */
const checkAndSaveUrlParameters = async (parsedSearchQuery: QueryString.ParsedQs): Promise<void> => {
  // CHECK ALL NEEDED PARAMS (alphabetically ordered)
  if (parsedSearchQuery['adjustable_product'])
    store.dispatch(
      updateMarketingData({ key: MarketingDataKeys.ADJUSTABLE_PRODUCT, value: parsedSearchQuery['adjustable_product'] === 'true' })
    )

  if (parsedSearchQuery['available_products']) {
    let availableProducts: AvailableProducts
    const availableProductsFromQuery = parsedSearchQuery['available_products']
    if (isValidAvailableProducts(availableProductsFromQuery)) {
      availableProducts = availableProductsFromQuery
    } else {
      const arrayOfAvailableProducts = Array.isArray(availableProductsFromQuery)
        ? (availableProductsFromQuery as string[])
        : typeof availableProductsFromQuery === 'string'
          ? availableProductsFromQuery.trim().split(',')
          : []

      availableProducts = arrayOfAvailableProducts.reduce(
        (prev: AvailableProducts, curr) => {
          if (curr && typeof curr === 'string' && isAvailableSalesProduct(curr)) {
            const productAvailability = PRODUCTS_CONFIG[curr].availableInRegions

            // Add the product to the regions in which it's available
            if (productAvailability.includes(REGIONS.flanders)) prev[REGIONS.flanders].push(curr)
            if (productAvailability.includes(REGIONS.wallonia)) prev[REGIONS.wallonia].push(curr)
            if (productAvailability.includes(REGIONS.brussels)) prev[REGIONS.brussels].push(curr)
          }

          return prev
        },
        {
          [REGIONS.flanders]: [],
          [REGIONS.brussels]: [],
          [REGIONS.wallonia]: []
        }
      )
    }

    if (availableProducts && Object.values(availableProducts).some((products) => products.length > 0)) {
      store.dispatch(updateMarketingData({ key: MarketingDataKeys.AVAILABLE_PRODUCTS, value: availableProducts }))
    }
  }

  if (parsedSearchQuery['agent_id'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.AGENT_ID, value: parsedSearchQuery['agent_id'] as string }))

  if (parsedSearchQuery['banner'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.BANNER, value: parsedSearchQuery['banner'] as string }))

  if (parsedSearchQuery['call_campaign'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.CALL_CAMPAIGN, value: parsedSearchQuery['call_campaign'] as string }))

  if (parsedSearchQuery['call_partner_account'])
    store.dispatch(
      updateMarketingData({ key: MarketingDataKeys.CALL_PARTNER_ACCOUNT, value: parsedSearchQuery['call_partner_account'] as string })
    )

  if (parsedSearchQuery['campaign_name'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.CAMPAIGN_NAME, value: parsedSearchQuery['campaign_name'] as string }))

  if (parsedSearchQuery[UrlSearchParams.DP_EAN_ELECTRICITY]) {
    store.dispatch(
      updateInput({
        group: InputGroupKeys.METER_DETAILS,
        key: MeterDetailsFieldKeys.ELECTRICITY,
        value: {
          ...store.getState().customer.inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.ELECTRICITY],
          ean: parsedSearchQuery[UrlSearchParams.DP_EAN_ELECTRICITY] as string
        }
      })
    )
  }

  if (parsedSearchQuery[UrlSearchParams.DP_EAN_GAS]) {
    store.dispatch(updateInput({ group: InputGroupKeys.DELIVERY_ADDRESS, key: DeliveryAddressFieldKeys.NEEDS_GAS, value: true }))
    store.dispatch(
      updateInput({
        group: InputGroupKeys.METER_DETAILS,
        key: MeterDetailsFieldKeys.GAS,
        value: {
          ...store.getState().customer.inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.GAS],
          ean: parsedSearchQuery[UrlSearchParams.DP_EAN_GAS] as string
        }
      })
    )
  }

  if (
    parsedSearchQuery[UrlSearchParams.DP_STREET] &&
    parsedSearchQuery[UrlSearchParams.DP_NUMBER] &&
    parsedSearchQuery[UrlSearchParams.DP_ZIP] &&
    parsedSearchQuery[UrlSearchParams.DP_CITY]
  ) {
    store.dispatch(
      updateInput({
        group: InputGroupKeys.DELIVERY_ADDRESS,
        key: DeliveryAddressFieldKeys.ADDRESS,
        value: {
          [AddressFields.STREET_NAME]: parsedSearchQuery[UrlSearchParams.DP_STREET] as string,
          [AddressFields.STREET_NUMBER]: parsedSearchQuery[UrlSearchParams.DP_NUMBER] as string,
          [AddressFields.STREET_BOX]: parsedSearchQuery[UrlSearchParams.DP_BOX]
            ? (parsedSearchQuery[UrlSearchParams.DP_BOX] as string)
            : undefined,
          [AddressFields.POSTAL_CODE]: parsedSearchQuery[UrlSearchParams.DP_ZIP] as string,
          [AddressFields.TOWN_NAME]: parsedSearchQuery[UrlSearchParams.DP_CITY] as string
        }
      })
    )
  }

  if (parsedSearchQuery['ean_code_mandatory'])
    store.dispatch(
      updateMarketingData({ key: MarketingDataKeys.EAN_CODE_MANDATORY, value: parsedSearchQuery['ean_code_mandatory'] === 'true' })
    )

  if (parsedSearchQuery['event_name'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.EVENT_NAME, value: parsedSearchQuery['event_name'] as string }))

  if (parsedSearchQuery['event_id'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.EVENT_ID, value: parsedSearchQuery['event_id'] as string }))

  if (parsedSearchQuery['execute_credit_check'])
    store.dispatch(
      updateMarketingData({ key: MarketingDataKeys.EXECUTE_CREDIT_CHECK, value: parsedSearchQuery['execute_credit_check'] !== 'false' })
    )

  if (parsedSearchQuery['gclid']) {
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.GCLID, value: parsedSearchQuery['gclid'] as string }))
    createCookie(CookieKeys.GCLID, parsedSearchQuery['gclid'] as string, 90)
  }

  if (parsedSearchQuery['no_optin_one'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.NO_OPTIN_ONE, value: parsedSearchQuery['no_optin_one'] === 'true' }))

  if (parsedSearchQuery[UrlSearchParams.OPPORTUNITY_ID]) {
    const idFromQuery = parsedSearchQuery[UrlSearchParams.OPPORTUNITY_ID]
    const opportunityId = Array.isArray(idFromQuery) ? (idFromQuery?.[0] as string) : (idFromQuery as string)

    store.dispatch(updateOpportunityId({ opportunityId }))

    // Should always be executed after opportunityId is stored as we need the opportunityId to fetch the simulation params from the API
    if (parsedSearchQuery['fetchSimulation'] === 'true' && opportunityId) {
      store.dispatch(updateMarketingData({ key: MarketingDataKeys.FETCH_SIMULATION_FLOW, value: true }))
      loadDataByOpportunityId(opportunityId, Router.locale as Language)
    }
  }

  if (parsedSearchQuery['partner_id'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.PARTNER_ID, value: parsedSearchQuery['partner_id'] as string }))

  if (parsedSearchQuery['plenty_opportunity_id']) {
    const plentyOpportunityId = parsedSearchQuery['plenty_opportunity_id'] as string
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.PLENTY_OPPORTUNITY_ID, value: plentyOpportunityId }))
    store.dispatch(setFlowAlternative({ key: FlowAlternatives.PLENTY, value: true }))
    store.dispatch(setShowPlentyPropositions(true))
  }

  if (parsedSearchQuery['ask_previous_supplier']) {
    const value = parsedSearchQuery['ask_previous_supplier'] === 'true'
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.PREVIOUS_SUPPLIER, value }))
  }

  if (typeof parsedSearchQuery['optin_cta'] === 'string') {
    const matchingOptinCta = Object.values(OPT_IN_CTAS).find(
      (cta) => cta.toLowerCase() === (parsedSearchQuery['optin_cta'] as string)?.toLowerCase()
    )
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.OPTIN_CTA, value: matchingOptinCta }))
    if (matchingOptinCta === OPT_IN_CTAS.contactMe)
      store.dispatch(setFlowAlternative({ key: FlowAlternatives.TELESALES_CAMPAIGN, value: true }))
  }

  if (parsedSearchQuery['promocode']) {
    const referralCodeFromQueryString = String(parsedSearchQuery['promocode'])

    try {
      const { code, valid } = await setPromoOrReferralCodeInStore(referralCodeFromQueryString)

      if (valid) {
        if (parsedSearchQuery['producer-referral'] === '1') {
          const bannerNL = `Stap over op lokale stroom en krijg €50 korting met de promocode ${code}`
          const bannerFR = `Passez à l'électricité locale et bénéficiez d'une réduction de 50 euros avec le code promo ${code}`

          store.dispatch(
            updateMarketingData({ key: MarketingDataKeys.BANNER, value: Router.locale === Language.DUTCH ? bannerNL : bannerFR })
          )
        }
      }
    } catch (e) {
      throw new Error(e)
    }
  }

  if (parsedSearchQuery[UrlSearchParams.REFERRAL_CODE]) {
    const referralCodeFromQueryString = String(parsedSearchQuery[UrlSearchParams.REFERRAL_CODE])

    try {
      const { valid } = await setPromoOrReferralCodeInStore(referralCodeFromQueryString)

      if (valid) {
        // !! We also need to set the cashback promocode (= VIAVIA) because of the referral campaign !!
        void setPromoOrReferralCodeInStore('VIAVIA')
      }
    } catch (e) {
      throw new Error(e)
    }
  }

  if (parsedSearchQuery['reset_button'])
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.RESET_BUTTON, value: parsedSearchQuery['reset_button'] === 'true' }))

  if (parsedSearchQuery[UrlSearchParams.SALESFORCE_USER_ALIAS]) {
    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.SALESFORCE_USER_ALIAS,
        value: parsedSearchQuery[UrlSearchParams.SALESFORCE_USER_ALIAS] as string
      })
    )
  }

  if (parsedSearchQuery['sign_channel']) {
    const signChannels = Array.isArray(parsedSearchQuery['sign_channel'])
      ? (parsedSearchQuery['sign_channel'] as SignChannel[])
      : ([parsedSearchQuery['sign_channel']] as SignChannel[])

    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.SIGN_CHANNEL,
        value: signChannels
      })
    )
  }

  if (parsedSearchQuery['simulation_type']) {
    let simulationTypes = (parsedSearchQuery['simulation_type'] as SimulationType[]) || [SIMULATION_TYPES.pricingSimulation]

    // Ensure simulationType is an array & remove Bolt GO
    if (!Array.isArray(simulationTypes)) {
      simulationTypes = [simulationTypes].filter((type) => type !== SIMULATION_TYPES.boltGo)
    }

    // Save the possible simulation types to the marketing store
    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.SIMULATION_TYPE,
        value: simulationTypes
      })
    )

    // Set the initial (chosen) simulation type in the simulation store
    // Default 'Pricing Simulation' if available, otherwise the first one
    store.dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.CHOSEN_SIMULATION_TYPE,
        value: simulationTypes.includes(SIMULATION_TYPES.pricingSimulation) ? SIMULATION_TYPES.pricingSimulation : simulationTypes[0]
      })
    )
  }

  if (parsedSearchQuery[UrlSearchParams.SIMULATION_SALES_OFFICE]) {
    const simulationSo = parsedSearchQuery[UrlSearchParams.SIMULATION_SALES_OFFICE] as string
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: simulationSo }))
  }

  if (parsedSearchQuery[UrlSearchParams.SKIP_ITSME] === 'true') {
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.SKIP_ITSME, value: true }))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.IDENTIFICATION_METHOD, value: IDENTIFICATION_METHODS.default }))
  }

  if (parsedSearchQuery['source_type']) {
    const sourceType = parsedSearchQuery['source_type'] as string | string[]
    store.dispatch(
      updateMarketingData({ key: MarketingDataKeys.SOURCE_TYPE, value: Array.isArray(sourceType) ? sourceType[0] : sourceType })
    )
  }

  if (parsedSearchQuery[UrlSearchParams.DYNAMIC_TARIFF]) {
    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.DYNAMIC_TARIFF_DEFAULT_CHECKED,
        value: true
      })
    )
  }

  if (parsedSearchQuery[UrlSearchParams.SETTLEMENT_FREQUENCY_MONTHLY]) {
    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.SETTLEMENT_FREQUENCY_MONTHLY_BY_DEFAULT,
        value: true
      })
    )
  }

  if (parsedSearchQuery['subscribed'] === '1') createCookie(CookieKeys.SUBSCRIBED, 'true')

  if (parsedSearchQuery['trigger_events'] === '0')
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.TRIGGER_EVENTS, value: parsedSearchQuery['trigger_events'] as string }))

  if (parsedSearchQuery['unlock']) createCookie(CookieKeys.UNLOCK, parsedSearchQuery['unlock'] as string)

  // REDIRECTS - always at the end
  if (parsedSearchQuery['simulation'] && parsedSearchQuery['simulation'] === '1') {
    // Track 'Simulation Started' event
    trackGTMEvent(CommonGTMEvents.SIMULATION_STARTED)
    trackEvent({ category: 'simulation', action: 'start' })
    void Router.push(routes(Router.locale || Language.DUTCH).simulation)
  }
}

/**
 * Initializes the store subscription (executes given subscribe function everytime the store changes)
 * Saves marketing store to window session storage
 */
const initStoreSubscription = (): void => {
  store.subscribe(() => {
    const { marketing } = store.getState()
    setSessionStorageItem(SessionStorageKeys.MARKETING, JSON.stringify(marketing))
  })
}
